import { Check } from 'lucide-react';
import { WIZARD_STEPS, stepIndex, stepReachable, type StepId } from './wizardSteps';

/**
 * Progress rail for the new-employee wizard. Steps already passed are
 * clickable so HR can go back and change an answer; steps ahead are not,
 * because nothing can be assigned until the person exists.
 */
export function WizardRail({
  current,
  personId,
  onGo,
}: {
  current: StepId;
  personId: string | null;
  onGo: (id: StepId) => void;
}) {
  const currentIndex = stepIndex(current);

  return (
    <nav aria-label="New employee steps">
      <ol className="flex flex-wrap gap-1.5">
        {WIZARD_STEPS.map((step, i) => {
          const done = i < currentIndex;
          const active = step.id === current;
          const enabled = stepReachable(step.id, personId) && i <= currentIndex;
          return (
            <li key={step.id}>
              <button
                type="button"
                disabled={!enabled}
                aria-current={active ? 'step' : undefined}
                onClick={() => onGo(step.id)}
                className={[
                  'flex items-center gap-2 rounded-token-sm px-3 py-1.5 text-xs font-medium transition-colors',
                  active
                    ? 'bg-accent text-fg-on-accent'
                    : done
                      ? 'bg-success-soft/15 text-success-soft-fg hover:bg-success-soft/25'
                      : 'bg-surface-2 text-fg-dim',
                  enabled ? 'cursor-pointer' : 'cursor-default',
                ].join(' ')}
              >
                <span
                  className={[
                    'flex h-4 w-4 items-center justify-center rounded-full text-[10px] tabular-nums',
                    active ? 'bg-fg-on-accent/20' : 'bg-black/5 dark:bg-white/10',
                  ].join(' ')}
                >
                  {done ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                {step.label}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
